import React, { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle, ChevronLeft, ChevronRight, History, Mail, RefreshCw, Send, Settings, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BACKEND_URL } from './constants';
import { getToken } from '../../services/tokenService';

export default function NotificationsPanel({ onClose }) {
  const [tab, setTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadConfig = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/notifications/config`, { headers });
      setConfig(res.data);
    } catch { toast.error('Erreur chargement config'); }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/notifications/historique?page=${logsPage}&limit=20`, { headers });
      setLogs(res.data.entries || []);
      setLogsTotal(res.data.total || 0);
    } catch { toast.error('Erreur chargement historique'); }
    setLoading(false);
  };

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => { if (tab === 'historique') loadLogs(); }, [tab, logsPage]);

  const handleSaveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/recus-ess/notifications/config`, {
        enabled: config.enabled,
        intervals: config.intervals,
        admin_notify_email: config.admin_notify_email,
      }, { headers: { ...headers, 'Content-Type': 'application/json' } });
      toast.success('Configuration sauvegardee');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur sauvegarde'); }
    setSaving(false);
  };

  const handleTestRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/recus-ess/notifications/test-run`, {}, { headers });
      toast.success(res.data.message);
      loadConfig();
      if (tab === 'historique') loadLogs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur execution'); }
    setRunning(false);
  };

  const intervalLabels = { '-3': 'J-3 (3 jours avant)', '0': 'J-0 (jour J)', '3': 'J+3 (3 jours apres)' };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="notifications-panel">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Bell className="h-5 w-5" /> Notifications echeances</h3>
            <p className="text-blue-100 text-sm">Rappels automatiques par email (Brevo)</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><XCircle className="h-6 w-6" /></button>
        </div>
        {/* Tabs */}
        <div className="flex border-b">
          <button onClick={() => setTab('config')} className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab === 'config' ? 'border-b-2 border-blue-600 text-blue-700 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`} data-testid="notif-tab-config">
            <Settings className="h-4 w-4" /> Configuration
          </button>
          <button onClick={() => setTab('historique')} className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab === 'historique' ? 'border-b-2 border-blue-600 text-blue-700 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`} data-testid="notif-tab-historique">
            <History className="h-4 w-4" /> Historique
          </button>
        </div>
        <div className="p-6">
          {tab === 'config' && config && (
            <div className="space-y-5">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  {config.enabled ? <Bell className="h-5 w-5 text-blue-600" /> : <BellOff className="h-5 w-5 text-gray-400" />}
                  <div>
                    <p className="font-medium text-sm text-gray-900">Notifications automatiques</p>
                    <p className="text-xs text-gray-500">Execution quotidienne a 8h30</p>
                  </div>
                </div>
                <button onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${config.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                  data-testid="notif-toggle-enabled">
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Intervals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Intervalles de rappel</label>
                <div className="grid grid-cols-3 gap-3">
                  {[-3, 0, 3].map(iv => {
                    const active = (config.intervals || []).includes(iv);
                    return (
                      <button key={iv} onClick={() => {
                        const current = config.intervals || [];
                        setConfig(c => ({ ...c, intervals: active ? current.filter(v => v !== iv) : [...current, iv].sort((a, b) => a - b) }));
                      }} className={`p-3 rounded-xl border-2 text-center transition-all ${active ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                        data-testid={`notif-interval-${iv}`}>
                        <div className="text-lg font-bold">{iv < 0 ? `J${iv}` : iv === 0 ? 'J-0' : `J+${iv}`}</div>
                        <div className="text-xs mt-0.5">{intervalLabels[String(iv)]}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Admin email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email administrateur (fallback)</label>
                <input type="email" value={config.admin_notify_email || ''} onChange={e => setConfig(c => ({ ...c, admin_notify_email: e.target.value }))}
                  placeholder="contact@objectifscopoutremer.com" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="notif-admin-email" />
                <p className="text-xs text-gray-400 mt-1">Utilise lorsqu'aucun email beneficiaire n'est disponible</p>
              </div>

              {/* Last run info */}
              {config.last_run && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
                  <p className="text-blue-700"><strong>Derniere execution :</strong> {new Date(config.last_run).toLocaleString('fr-FR')}</p>
                  <p className="text-blue-600 mt-0.5">{config.last_run_sent || 0} email(s) envoye(s), {config.last_run_errors || 0} erreur(s)</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={handleTestRun} disabled={running} className="border-blue-300 text-blue-700" data-testid="notif-test-run">
                  {running ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Executer maintenant
                </Button>
                <Button onClick={handleSaveConfig} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="notif-save-config">
                  {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />} Sauvegarder
                </Button>
              </div>
            </div>
          )}

          {tab === 'historique' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-10"><RefreshCw className="h-6 w-6 animate-spin text-blue-400" /></div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-gray-500"><Mail className="h-12 w-12 mx-auto mb-3 opacity-20" /><p className="font-medium">Aucune notification envoyee</p></div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 mb-3">{logsTotal} notification(s) au total</p>
                  {logs.map((log, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${log.success ? 'bg-emerald-50/50 border-emerald-200' : 'bg-red-50/50 border-red-200'}`} data-testid={`notif-log-${i}`}>
                      <div className={`mt-0.5 p-1.5 rounded-full ${log.success ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {log.success ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">{log.type === 'acquitte' ? 'ACQUITTEE' : log.type?.replace('reminder_', '') || '-'}</Badge>
                          <span className="text-xs text-gray-400">{log.sent_at ? new Date(log.sent_at).toLocaleString('fr-FR') : '-'}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">{log.subject || '-'}</p>
                        <p className="text-xs text-gray-500">{log.email} {log.raison_sociale ? `- ${log.raison_sociale}` : ''}</p>
                      </div>
                    </div>
                  ))}
                  {logsTotal > 20 && (
                    <div className="flex justify-between items-center pt-3">
                      <Button variant="outline" size="sm" disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                      <span className="text-sm text-gray-500">Page {logsPage}</span>
                      <Button variant="outline" size="sm" disabled={logsPage * 20 >= logsTotal} onClick={() => setLogsPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

